<template>
  <div>
    <b-row
      v-if="!isReadOnly"
      class="pb-1"
    >
      <b-col class="d-flex justify-content-end">
        <e-button
          variant="primary"
          :text="$t('Adicionar')"
          @click="onShowSidebar"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-table
          ref="pix-table"
          show-empty
          responsive
          class="bordered"
          :fields="fields"
          :items="pixs"
          :empty-text="emptyTableMessage"
        >
          <template #cell(active)="row">
            <e-status-badge :status="row.item.active" />
          </template>

          <template #cell(action)="row">
            <e-grid-actions
              :show-update="true"
              :show-delete="true"
              @update="onShowSidebar(row)"
              @delete="deletePix(row)"
            />
          </template>
        </b-table>
      </b-col>
    </b-row>

    <e-sidebar
      v-if="!isReadOnly"
      :show="showSidebar"
      :fetching="loading"
      :title="isEdit ? $t('Alterar PIX') : $t('Novo PIX')"
      width="600px"
      @save="addBankClick"
      @hidden="showSidebar = false"
    >
      <template #content>
        <FormulateForm
          ref="formPix"
          name="formPix"
        >
          <b-card-actions
            :title="$t('PIX')"
            no-actions
          >
            <b-row>
              <b-col md="4">
                <FormulateInput
                  id="pix-type"
                  v-model="pixForm.type"
                  type="vue-select"
                  :label="$t('Tipo')"
                  validation="required"
                  class="required"
                  :placeholder="$t('Selecione')"
                  :options="pixTypes()"
                />
              </b-col>
              <b-col md="8">
                <FormulateInput
                  id="pix-key"
                  v-model="pixForm.key"
                  type="text"
                  :label="$t('Chave')"
                  validation="required"
                  class="required"
                />
              </b-col>
              <b-col md="8">
                <FormulateInput
                  id="pix-name"
                  v-model="pixForm.name"
                  type="text"
                  :label="$t('Nome')"
                  validation="required"
                  class="required"
                />
              </b-col>
            </b-row>
          </b-card-actions>

          <b-card-actions no-actions>
            <b-row align-h="end">
              <b-col md="2">
                <FormulateInput
                  id="pix-active"
                  v-model="pixForm.active"
                  type="switch"
                  :label="$t('Ativo?')"
                />
              </b-col>
            </b-row>
          </b-card-actions>
        </FormulateForm>
      </template>
    </e-sidebar>
  </div>
</template>

<script>
import { BTable, BRow, BCol } from 'bootstrap-vue'
import EButton from '@/views/components/EButton.vue'
import { pixTypes } from '@/mixins'
import BCardActions from '@/@core/components/b-card-actions/BCardActions.vue'
import ESidebar from '../ESidebar.vue'
import EGridActions from '../EGridActions.vue'
import EStatusBadge from '../EStatusBadge.vue'

const getInitialForm = () => ({
  id: null,
  localId: null,
  type: '',
  key: '',
  name: '',
  active: true,
})

export default {
  name: 'EBankPixTab',
  components: {
    BTable,
    BCol,
    BRow,
    EButton,
    ESidebar,
    BCardActions,
    EGridActions,
    EStatusBadge,
  },

  mixins: [pixTypes],

  props: {
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    pixs: {
      type: Array,
      required: true,
    },

    /**
     * Para personalização no componente, valores aceitos: None | Customer | Supplier
     */
    person: {
      type: String,
      required: false,
      default: 'None',
      validation(value) {
        return ['None', 'Customer', 'Supplier'].includes(value)
      },
    },
  },

  data() {
    return {
      loading: false,
      pixForm: getInitialForm(),
      showSidebar: false,
      isEdit: false,
    }
  },

  computed: {
    emptyTableMessage() {
      if (this.person === 'Customer')
        return this.$t('NENHUM PIX INFORMADO PARA ESTE CLIENTE ATÉ O MOMENTO')
      if (this.person === 'Supplier')
        return this.$t('NENHUM PIX INFORMADO PARA ESTE FORNECEDOR ATÉ O MOMENTO')

      return this.$t('NENHUM DADO BANCÁRIO INFORMADO ATÉ O MOMENTO')
    },

    fields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'action',
          class: 'text-center',
          thStyle: { width: '100px' },
          hide: this.isReadOnly,
        },
        {
          label: this.$t('Tipo'),
          key: 'type',
          class: 'text-left',
          formatter: val => this.getPixTypes[val],
        },
        {
          label: this.$t('Chave'),
          key: 'key',
          class: 'text-center',
        },
        {
          label: this.$t('Nome'),
          key: 'name',
          class: 'text-center',
        },
        {
          label: this.$t('Ativo'),
          key: 'active',
          class: 'text-center',
          thStyle: { width: '50px' },
        },
      ].filter(f => !f.hide)
    },
  },

  methods: {
    async onShowSidebar(row = null) {
      this.showSidebar = true
      this.cleanForm()

      if (row) {
        try {
          this.loading = true
          this.isEdit = true
          await new Promise(resolve => setTimeout(() => resolve(), 500))
          this.pixForm = { ...row.item }
        } finally {
          this.loading = false
        }
      }
    },

    addBankClick() {
      this.$refs.formPix.showErrors()
      if (this.$refs.formPix.hasErrors) {
        this.showInvalidDataMessage()
        return
      }

      if (this.isEdit) {
        this.$emit('update-pix', { ...this.pixForm })
      } else {
        this.$emit('add-pix', { ...this.pixForm })
      }

      this.showSidebar = false
      this.cleanForm()
    },

    async deletePix(row) {
      try {
        const confirm = await this.confirm()
        if (confirm) {
          const id = row.item.id || row.item.localId
          this.$emit('remove-pix', id)
        }
      } catch (error) {
        this.showError({ error })
      }
    },

    cleanForm() {
      this.pixForm = getInitialForm()
      this.isEdit = false
    },
  },
}
</script>

<style></style>
